import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import Alert from '@mui/material/Alert';
import { AlertColor } from '@mui/material';
import Snackbar from '@material-ui/core/Snackbar';
import { useTheme } from '../contexts/ThemeContext';
import i18n from '../i18n/i18n';
import { getSlaveInfo } from '../api/api';
import { useVendingMachine } from '../contexts/VendingMachineContext';
import Header from '../components/TopHeader';
import MennekesIcon from '../images/models/mennekes.png';
import ChademoIcon from '../images/models/chademo.png';
import CCS from '../images/models/ccs.png';
import LoadingOverlay from '../components/overlay/LoadingOverlay';
import { detachOrphanSlaves, formatSlaveLabel, isSlaveAvailable } from '../utils/slaveSelection';

const MODEL_IMAGES: Record<string, string> = {
  mennekes: MennekesIcon,
  chademo: ChademoIcon,
  ccs: CCS,
};

const Page = styled.div`
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderSlot = styled.div`
  flex-shrink: 0;
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  align-items: center;
`;

const LogoContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: rgba(0, 0, 0, 0.2) 0px -4px 4px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  min-height: 0;
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const ButtonsContainer = styled.div`
  margin: 20px 0 0 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
  overflow-y: auto;
  min-height: 0;
  width: 100%;
  padding-bottom: 16px;
`;

const StyledButton = styled(({ gradient, ...rest }) => <Button {...rest} />)`
  && {
    background: ${(props) => props.gradient || props.color};
    color: ${(props) => props.textColor};
    border-width: 0;
    border-radius: ${(props) => (props.gradient ? '12px' : '8px')};
    padding: 16px 20px;
    width: 95%;
    height: 54px;
    justify-content: space-between;

    &&:hover,
    &&:focus,
    &&:active {
      background: ${({ gradient, color }) => gradient || color};
      color: ${({ textColor }) => textColor};
    }

    //NECESSARY FOR DISABLED STYLES NOT LOOK LIKE EMPTY BUTTON
    &&.Mui-disabled {
      color: white;
      background-color: lightgray;
    }

    &:disabled {
      color: white;
      background-color: lightgray;
    }
  }
`;

const Message = styled.span<{ font?: string, textColor?: string }>`
  text-align: center;
  font: ${(props) => `1.6em ${props.font}`};
  color: ${(props) => props.textColor || 'rgba(77,76,77,255)'};
  white-space: pre-line;
`;

type SelectSocketProps = {
  initialParentId: string | null;
  // The level the customer cannot go above: the sticker's charger (or the top level)
  scopeRootId: string | null;
  initialNotice?: string | null;
  onSelected: (slaveId: string) => void;
};

// Connector picker for EV chargers, rendered by VMView inside the /home flow
function SelectSocket({
  initialParentId, scopeRootId, initialNotice, onSelected,
}: SelectSocketProps) {
  const { theme, themeName } = useTheme();
  const [stateLoading, setStateLoading] = useState(false);
  const [parentMachineId, setParentMachineId] = useState<string | null>(initialParentId);
  const [visibleMachines, setVisibleMachines] = useState<any[]>([]);
  const { vendingMachineInfo } = useVendingMachine();
  const machines: any[] = useMemo(
    () => detachOrphanSlaves(vendingMachineInfo?.vendingMachineSlaves || []),
    [vendingMachineInfo],
  );
  const levelSlave = machines.find((machine) => machine.id === parentMachineId);
  // The charger the customer drilled into; the top level shows no slave
  const levelLabel = levelSlave ? formatSlaveLabel(machines, levelSlave, vendingMachineInfo?.identificationNumber) : '';
  const isSocketLevel = visibleMachines.length > 0 && visibleMachines.every(
    (machine) => !machines.some((m) => m.parentId === machine.id),
  );

  const [notificationVisible, setNotificationVisible] = useState(false);
  const [notificationClass, setNotificationClass] = useState('success');
  const [notificationMessage, setNotificationMessage] = useState('');

  const showError = () => {
    setNotificationClass('error');
    setNotificationMessage('error_occured_try_later');
    setNotificationVisible(true);
  };

  const showInUseError = () => {
    setNotificationClass('warning');
    setNotificationMessage('socket_in_use');
    setNotificationVisible(true);
  };

  useEffect(() => {
    if (initialNotice) {
      setNotificationClass(initialNotice === 'socket_in_use' ? 'warning' : 'error');
      setNotificationMessage(initialNotice);
      setNotificationVisible(true);
    }
  }, [initialNotice]);

  // True when the connector cannot be used; the notice (in use, or the check failed) is already shown
  const checkInUse = useCallback(async (slaveId: string) => {
    try {
      setStateLoading(true);
      const response = await getSlaveInfo(slaveId);
      const inUse = !isSlaveAvailable(response.state);
      if (inUse) {
        showInUseError();
      }
      return inUse;
    } catch (e) {
      showError();
      return true;
    } finally {
      setStateLoading(false);
    }
  }, []);

  const handleSelection = useCallback(
    async (selectedMachine) => {
      // The level lives in local state only; VMView stores the final connector
      if (machines.some((machine) => machine.parentId === selectedMachine.id)) {
        setParentMachineId(selectedMachine.id);
      } else {
        if (await checkInUse(selectedMachine.id)) {
          return;
        }
        onSelected(selectedMachine.id);
      }
    },
    [machines, checkInUse, onSelected],
  );

  const atScopeRoot = parentMachineId === scopeRootId;

  const handleBack = useCallback(() => {
    if (parentMachineId === scopeRootId) {
      return;
    }
    const localParentMachine = machines.find((machine) => machine.id === parentMachineId);
    setParentMachineId(localParentMachine ? localParentMachine.parentId : null);
  }, [machines, parentMachineId, scopeRootId]);

  useEffect(() => {
    const newVisibleMachines = machines.filter((machine) => machine.parentId === parentMachineId);
    setVisibleMachines(newVisibleMachines);
  }, [machines, parentMachineId]);

  const normalizeModelName = (name: string) => {
    if (name.toLowerCase().includes('mennekes')) {
      return 'mennekes';
    } if (name.toLowerCase().includes('ccs')) {
      return 'ccs';
    } if (name.toLowerCase().includes('chademo')) {
      return 'chademo';
    }
    return '';
  };

  return (
    <Page>
      <HeaderSlot>
        <Header
          showVm
          userLogo
          showBackButton={!atScopeRoot}
          backButtonColor={theme.buttonColor}
          onBack={handleBack}
          color={themeName ? theme.iconColor : undefined}
          slaveLabel={levelLabel}
        />
      </HeaderSlot>
      <Root>
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          autoHideDuration={5000}
          open={notificationVisible}
          onClose={() => setNotificationVisible(false)}
        >
          <Alert
            onClose={() => setNotificationVisible(false)}
            severity={notificationClass as AlertColor}
            variant="filled"
            sx={{ width: '100%', backgroundColor: theme.buttonColor }}
          >
            {i18n.t(notificationMessage)}
          </Alert>
        </Snackbar>
        <Content>
          <LoadingOverlay
            open={stateLoading}
            color={theme.buttonColor}
            messageId="verify_socket_availability"
          />
          <Message
            font={theme.bodyFont}
            textColor={themeName === 'helexia' ? theme.cardTextColor : undefined}
            style={{ marginTop: 20 }}
          >
            {i18n.t(isSocketLevel ? (themeName === 'helexia' ? 'select_outlet' : 'select_connector') : 'select_charger')}
          </Message>
          <ButtonsContainer>
            {visibleMachines.map((machine) => {
              const machineName = machine
                ? `${machine.number || 'X'} - ${machine?.name || 'Unknown'}`
                : 'X - Unknown';

              const modelName = normalizeModelName(machine.name);

              return (
                <StyledButton
                  style={{
                    fontSize: 'small',
                    textAlign: 'left',
                  }}
                  key={machine.id}
                  color={theme.buttonColor}
                  textColor={themeName === 'helexia' ? 'white' : theme.buttonTextColor}
                  gradient={themeName === 'helexia' ? 'linear-gradient(90deg, #fbd802 0%, #f8c64b 100%)' : undefined}
                  variant="outlined"
                  disabled={stateLoading}
                  onClick={() => handleSelection(machine)}
                  disableRipple
                >
                  {machineName}
                  <div>
                    {modelName && MODEL_IMAGES[modelName] && (
                      <img
                        src={MODEL_IMAGES[modelName]}
                        alt="socket model"
                        style={{
                          width: '24px',
                          height: 'auto',
                          marginLeft: '10px',
                          marginTop: '5px',
                        }}
                      />
                    )}
                  </div>
                </StyledButton>
              );
            })}
          </ButtonsContainer>
        </Content>
      </Root>
      {(theme.logo && themeName !== 'helexia') && (
        <LogoContainer>
          <img
            src={theme.logo}
            alt="logo"
            style={{
              width: theme.iconWidth,
              height: theme.iconHeight,
              marginBottom: '6px',
              marginTop: '6px',
            }}
          />
        </LogoContainer>
      )}
    </Page>
  );
}

export default SelectSocket;
